import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import { ProOnly } from '../label/ProOnly';

const TabContainer = Styled.div`
`;

const TabGroup = Styled.div`
`;

const Tab = Styled.button`
  font-family: ${Theme.fontFamily};
  font-size: ${Theme.fontSizeText};
  border-radius: 8px 8px 0px 0px;
  border: 0;
  background-color: #459ad2;
  color: rgb(255 255 255 / 65%);

  cursor: pointer;
  line-height: 17px;
  margin-right: 5px;
  text-align: left;
  padding: 10px 15px 10px 15px;

  &:focus {
    outline: none;
  }

  &.active {
    background-color: ${Theme.blue};
    color: ${Theme.white};
  }

  &.disabled {
    background-color: rgb(224 156 54);
    cursor: default;
    display: inline-flex;
    padding-bottom: 7px;
  }
`;

const TabContent = Styled.div`
  background-color: ${Theme.blue};
  display: none;
  padding: 10px;

  &.active {
    display: block;
  }
`;

/**
 * Need to use with NecoTab
 */
const StyledNekoTabs = props => {

  const [isActiveTabIndex, setIsActiveTabIndex] = React.useState(0);

  React.useEffect(() => {
    if (props.keepTabOnReload) {
      const url = new URL(window.location.href);
      const title = url.searchParams.get('nekoTab');
      if (!title) {
        setIsActiveTabIndex(0);
        return;
      }
      const searchTitle = decodeURIComponent(title);
      const index = React.Children.toArray(props.children).map(c => c.props.title.replace(' ', '')).indexOf(searchTitle);
      if (index === -1) {
        setIsActiveTabIndex(0);
        return;
      }
      setIsActiveTabIndex(index);
      return;
    }
    setIsActiveTabIndex(0);
  }, [])

  const tabAttributes = React.Children.map(props.children, (child, i) => {
    const title = child.props.title || ("Untitled Tab " + (i + 1));
    const key = child.props.key || 'neko-tab-' + title.toLowerCase();
    const onClick = child.props.onClick ? child.props.onClick : null;
    const requirePro = !props.isPro && (child.props.requirePro || false);
    return { key, title, onClick, requirePro };
  });

  const children = React.Children.map(props.children, (child, i) => {
    return React.cloneElement(child, {
      isActive: i === isActiveTabIndex
    });
  });

  const keepTab = (title) => {
    if (history.pushState && title) {
      let searchParams = new URLSearchParams(window.location.search);
      const value = encodeURIComponent(title.replace(' ', ''));
      searchParams.set('nekoTab', value);
      let newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?' + searchParams.toString();
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }

  const onTabClick = (tabIndex, tabAttr, ev) => {
    if (tabAttr.requirePro) return;
    setIsActiveTabIndex(tabIndex);
    if (props.onChange) {
      props.onChange(tabIndex, tabAttr, ev);
    }
    if (props.keepTabOnReload) {
      keepTab(tabAttr.title);
    }
  };

  return (
    <TabContainer>
      <TabGroup>
        {tabAttributes.map((attr, i) =>
          <Tab 
            key={attr.key} 
            className={`neko-tab-title ${i === isActiveTabIndex ? 'active' : ''} ${attr.requirePro ? 'disabled' : ''}`} 
            onClick={(ev) => { onTabClick(i, attr, ev) }} >
            {attr.title}<ProOnly className="inline" style={{ marginLeft: 10, marginRight: -5, top: -1 }} show={attr.requirePro} />
          </Tab>
        )}
      </TabGroup>
      {children}
    </TabContainer>
  );
};


/**
 * @oaram {string} title
 * @param {bool} isActive
 */
const StyledNekoTab = props => {

  const { children, isActive } = props;

  return (
    <TabContent className={`${isActive ? 'active' : ''}`}>
      {!!isActive && children}
    </TabContent>
  );
};

const NekoTabs = (props) => {
  return (<StyledNekoTabs {...props} />);
};

NekoTabs.propTypes = {
  isPro: PropTypes.bool,
  onChange: PropTypes.func
};

NekoTabs.defaultProps = {
  isPro: false,
  onChange: undefined
};

const NekoTab = (props) => {
  return (<StyledNekoTab {...props} />);
};

NekoTab.propTypes = {
  isActive: PropTypes.bool,
  requirePro: PropTypes.bool,
  title: PropTypes.string
};

NekoTab.defaultProps = {
  isActive: false,
  requirePro: false,
  title: undefined
};

export { NekoTab, NekoTabs };
